/* ============================================================
   Podcasts — filtrage chronologique + thèmes
   Fichier : claude-podcast-filters.js

   Web Component plutôt qu'IIFE : la section expose une dizaine de réglages
   d'éditeur, et chaque modification la re-rend. `connectedCallback` se
   redéclenche gratuitement, là où une IIFE branchée sur DOMContentLoaded
   resterait morte après un re-render (il faudrait écouter shopify:section:load).

   L'état initial est déjà correct dans le HTML servi par Liquid : ce script ne
   fait que prendre le relais des interactions, jamais l'affichage de départ.

   Les libellés composés sont en français : boutique mono-locale (be-healthy.fr).
   Les textes paramétrables (état vide, « tous les podcasts », réinitialisation)
   viennent des réglages de section via data-attributes.
   ============================================================ */

class PodcastFilters extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.itemsEl = this.querySelector('[data-podcast-items]');
    if (!this.itemsEl) return;

    this.countEl = this.querySelector('[data-podcast-count]');
    this.emptyEl = this.querySelector('[data-podcast-empty]');
    this.resetEl = this.querySelector('[data-podcast-reset]');
    this.yearsEl = this.querySelector('[data-podcast-years]');
    this.allBtn = this.querySelector('.podcast-chrono__all');
    this.chronoEl = this.querySelector('.podcast-chrono');
    this.tagsEl = this.querySelector('.podcast-tags');
    this.chronoToggle = this.querySelector('[data-podcast-toggle="chrono"]');
    this.tagsToggle = this.querySelector('[data-podcast-toggle="tags"]');
    // Libellés « au repos » des deux déclencheurs, lus dans le markup pour
    // rester pilotés par les réglages de section.
    this.chronoDefaultLabel = this.toggleLabelText(this.chronoToggle);
    this.tagsDefaultLabel = this.toggleLabelText(this.tagsToggle);
    this.mobile = window.matchMedia('(max-width: 989px)');

    // Mêmes noms de mois que Liquid : les mois ajoutés ici (années absorbées
    // depuis les pages suivantes) doivent être rendus à l'identique.
    this.monthNames = (this.dataset.months || '').split(',').filter(Boolean);

    this.allLabel = this.dataset.allLabel || 'Tous les podcasts';
    this.emptyText = this.dataset.emptyText || 'Aucun podcast ne correspond à cette sélection.';
    this.defaultPeriod = this.dataset.defaultMonth || '';

    this.refreshNodes();
    this.readHash();

    this._onClick = this.onClick.bind(this);
    this._onHash = () => {
      this.readHash();
      this.apply();
    };
    this.addEventListener('click', this._onClick);
    window.addEventListener('hashchange', this._onHash);

    this.apply({ initial: true });

    // Deux frames : le premier paint joue l'apparition décalée émise par
    // Liquid, puis .is-ready la neutralise pour toutes les mises à jour
    // suivantes — au changement de filtre, seul le fondu du conteneur agit.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('is-ready'));
    });

    this.loadRemainingPages();
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHash);
  }

  refreshNodes() {
    this.items = Array.from(this.itemsEl.querySelectorAll('.podcast-item'));
    this.monthBtns = Array.from(this.querySelectorAll('.podcast-chrono__month'));
    this.yearBtns = Array.from(this.querySelectorAll('.podcast-chrono__year-toggle'));
    this.pills = Array.from(this.querySelectorAll('.podcast-tags__pill'));
  }

  /* ── Interactions ─────────────────────────────────────────── */

  onClick(event) {
    const toggle = event.target.closest('[data-podcast-toggle]');
    if (toggle && this.contains(toggle)) {
      this.togglePanel(toggle.dataset.podcastToggle);
      return;
    }

    const widen = event.target.closest('[data-podcast-widen]');
    if (widen && this.contains(widen)) {
      if (widen.dataset.podcastWiden === 'period') this.period = '';
      else {
        this.period = '';
        this.tag = null;
      }
      this.syncExpanded();
      this.apply();
      return;
    }

    const reset = event.target.closest('[data-podcast-reset], .podcast-chrono__all');
    if (reset && this.contains(reset)) {
      this.period = '';
      this.tag = null;
      if (this.mobile.matches) this.closePanels();
      this.apply();
      return;
    }

    const yearBtn = event.target.closest('.podcast-chrono__year-toggle');
    if (yearBtn && this.contains(yearBtn)) {
      const y = yearBtn.dataset.year;
      if (this.period === y) {
        this.period = '';
        this.setExpandedYear(null);
      } else {
        this.period = y;
        this.setExpandedYear(y);
      }
      this.apply();
      return;
    }

    const monthBtn = event.target.closest('.podcast-chrono__month');
    if (monthBtn && this.contains(monthBtn)) {
      const mk = monthBtn.dataset.month;
      this.period = this.period === mk ? '' : mk;
      this.syncExpanded();
      // Un mois choisi clôt la sélection : on rend l'écran aux épisodes.
      // Choisir une ANNÉE laisse au contraire le panneau ouvert, puisque
      // l'intention y est le plus souvent d'affiner ensuite par mois.
      if (this.mobile.matches) this.closePanels();
      this.apply();
      return;
    }

    const pill = event.target.closest('.podcast-tags__pill');
    if (pill && this.contains(pill)) {
      const key = pill.dataset.tag;
      // Changer de thème ne touche jamais au filtre chronologique : on préfère
      // montrer l'état vide, qui propose d'élargir, plutôt que de modifier
      // silencieusement ce que le visiteur a demandé.
      this.tag = this.tag === key ? null : key;
      if (this.mobile.matches) this.closePanels();
      this.apply();
    }
  }

  // Un seul panneau ouvert à la fois : deux sélecteurs déployés simultanément
  // repousseraient le premier épisode hors de l'écran.
  togglePanel(which) {
    const open = which === 'chrono' ? this.chronoEl : this.tagsEl;
    const other = which === 'chrono' ? this.tagsEl : this.chronoEl;
    if (!open) return;
    const willOpen = !open.classList.contains('is-open');
    this.setPanel(open, willOpen);
    if (other) this.setPanel(other, false);
  }

  setPanel(wrapper, open) {
    wrapper.classList.toggle('is-open', open);
    const toggle = wrapper.querySelector('[data-podcast-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  closePanels() {
    if (this.chronoEl) this.setPanel(this.chronoEl, false);
    if (this.tagsEl) this.setPanel(this.tagsEl, false);
  }

  setExpandedYear(year) {
    this.yearBtns.forEach((btn) => {
      const on = btn.dataset.year === year;
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      const list = document.getElementById(btn.getAttribute('aria-controls'));
      if (list) list.hidden = !on;
    });
  }

  syncExpanded() {
    this.setExpandedYear(this.period ? this.period.slice(0, 4) : null);
  }

  /* ── Filtrage ─────────────────────────────────────────────── */

  apply(options) {
    const initial = !!(options && options.initial);
    const period = this.period;
    const tag = this.tag;
    const monthCounts = {};
    const tagCounts = {};
    let visible = 0;
    let rank = 0;

    this.items.forEach((item) => {
      const month = item.dataset.month || '';
      const tags = (item.dataset.tags || '').split(' ').filter(Boolean);
      const okPeriod = !period || month.indexOf(period) === 0;
      const okTag = !tag || tags.indexOf(tag) !== -1;

      // Compteurs croisés : chaque axe reflète le filtre de l'autre, sinon les
      // chiffres promettraient des résultats que l'intersection ne donne pas.
      if (okTag) monthCounts[month] = (monthCounts[month] || 0) + 1;
      if (okPeriod) {
        tags.forEach((t) => {
          tagCounts[t] = (tagCounts[t] || 0) + 1;
        });
      }

      const show = okPeriod && okTag;
      item.classList.toggle('podcast-item--off', !show);
      if (show) {
        item.style.setProperty('--reveal-i', Math.min(rank, 8));
        rank += 1;
        visible += 1;
      }
    });

    this.paintChrono(monthCounts);
    this.paintPills(tagCounts);
    this.paintResults(visible, initial);
    this.paintToggles();

    if (!initial) {
      this.writeHash();
      this.flash();
    }
  }

  paintChrono(monthCounts) {
    this.monthBtns.forEach((btn) => {
      const mk = btn.dataset.month;
      const n = monthCounts[mk] || 0;
      btn.setAttribute('aria-pressed', this.period === mk ? 'true' : 'false');
      btn.classList.toggle('is-empty', n === 0);
      const count = btn.querySelector('.podcast-chrono__count');
      if (count) count.textContent = n;
    });

    this.yearBtns.forEach((btn) => {
      const y = btn.dataset.year;
      const list = document.getElementById(btn.getAttribute('aria-controls'));
      let total = 0;
      if (list) {
        list.querySelectorAll('.podcast-chrono__month').forEach((m) => {
          total += monthCounts[m.dataset.month] || 0;
        });
      }
      btn.setAttribute('aria-pressed', this.period === y ? 'true' : 'false');
      btn.classList.toggle('is-empty', total === 0);
    });

    if (this.allBtn) {
      this.allBtn.setAttribute('aria-pressed', !this.period && !this.tag ? 'true' : 'false');
    }
  }

  paintPills(tagCounts) {
    this.pills.forEach((pill) => {
      const key = pill.dataset.tag;
      pill.setAttribute('aria-pressed', this.tag === key ? 'true' : 'false');
      pill.classList.toggle('is-empty', (tagCounts[key] || 0) === 0);
    });
  }

  paintResults(visible, initial) {
    // Au premier passage le compteur est déjà rendu par Liquid : on n'y touche
    // pas, pour qu'un aria-live="polite" n'annonce rien à l'arrivée.
    if (this.countEl && !initial) this.countEl.textContent = this.countText(visible);
    if (this.resetEl) this.resetEl.hidden = !this.period && !this.tag;
    this.paintEmpty(visible);
  }

  countText(n) {
    if (n === 0) return 'Aucun podcast';
    let text = n + ' podcast' + (n > 1 ? 's' : '');
    if (this.period) text += ' en ' + this.periodLabel(this.period);
    if (this.tag) text += ' sur le thème ' + this.tagLabel(this.tag);
    return text;
  }

  paintEmpty(visible) {
    if (!this.emptyEl) return;

    if (visible > 0) {
      this.emptyEl.hidden = true;
      this.emptyEl.textContent = '';
      return;
    }

    this.emptyEl.textContent = '';
    this.emptyEl.hidden = false;

    const message = document.createElement('p');
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'podcast-list__empty-action';

    // Un cul-de-sac devient un chemin : plutôt que d'annoncer l'absence de
    // résultat, on propose l'élargissement qui a le plus de chances d'aboutir.
    if (this.period && this.tag) {
      message.textContent = 'Aucun podcast en ' + this.periodLabel(this.period) + ' sur ce thème.';
      action.textContent = 'Voir tous les podcasts « ' + this.tagLabel(this.tag) + ' »';
      action.dataset.podcastWiden = 'period';
    } else {
      message.textContent = this.emptyText;
      action.textContent = this.allLabel;
      action.dataset.podcastWiden = 'all';
    }

    this.emptyEl.append(message, action);
  }

  // Les déclencheurs repliés portent l'état courant : « Juin 2026 » plutôt que
  // « Archives », « Sommeil » plutôt que « Thèmes ». C'est ce qui rend le
  // repli acceptable — on sait ce qui filtre sans rien déplier.
  paintToggles() {
    if (this.chronoToggle) {
      const label = this.period ? this.periodLabel(this.period) : this.chronoDefaultLabel;
      this.setToggleLabel(this.chronoToggle, label.charAt(0).toUpperCase() + label.slice(1));
    }
    if (this.tagsToggle) {
      this.setToggleLabel(this.tagsToggle, this.tag ? this.tagLabel(this.tag) : this.tagsDefaultLabel);
    }
  }

  toggleLabelText(toggle) {
    if (!toggle) return '';
    const label = toggle.querySelector('[data-podcast-toggle-label]');
    return label ? label.textContent.trim() : '';
  }

  setToggleLabel(toggle, text) {
    const label = toggle.querySelector('[data-podcast-toggle-label]');
    if (label) label.textContent = text;
  }

  // Fondu court qui signale que la liste a changé — remplace le rejeu de
  // l'apparition décalée, agressif et coûteux à chaque clic.
  flash() {
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
    this.itemsEl.classList.add('is-updating');
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => this.itemsEl.classList.remove('is-updating'), 150);
  }

  /* ── Libellés ─────────────────────────────────────────────── */

  periodLabel(period) {
    if (period.length === 4) return period;

    const btn = this.monthBtns.find((b) => b.dataset.month === period);
    if (btn) {
      const name = btn.querySelector('.podcast-chrono__month-name');
      if (name) return name.textContent.trim().toLowerCase() + ' ' + period.slice(0, 4);
    }
    return this.monthName(period).toLowerCase() + ' ' + period.slice(0, 4);
  }

  monthName(monthKey) {
    const index = parseInt(monthKey.slice(5, 7), 10) - 1;
    const name = this.monthNames[index];
    if (!name) return monthKey;
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  tagLabel(key) {
    const pill = this.pills.find((p) => p.dataset.tag === key);
    return pill ? pill.textContent.trim() : key;
  }

  /* ── Partage par l'URL ────────────────────────────────────── */

  readHash() {
    this.period = this.defaultPeriod;
    this.tag = null;

    const raw = window.location.hash.replace(/^#/, '');
    if (!raw) {
      this.syncExpanded();
      return;
    }

    const params = {};
    raw.split('&').forEach((pair) => {
      const i = pair.indexOf('=');
      if (i > 0) {
        try {
          params[decodeURIComponent(pair.slice(0, i))] = decodeURIComponent(pair.slice(i + 1));
        } catch (error) {
          /* paramètre mal encodé : ignoré */
        }
      }
    });

    // Une valeur qui ne correspond à aucun contrôle présent est ignorée : un
    // lien vers un mois disparu depuis retombe sur l'état par défaut.
    if (typeof params.mois === 'string') {
      if (params.mois === 'tout') this.period = '';
      else if (this.isKnownPeriod(params.mois)) this.period = params.mois;
    }
    if (typeof params.theme === 'string' && this.pills.some((p) => p.dataset.tag === params.theme)) {
      this.tag = params.theme;
    }

    this.syncExpanded();
  }

  isKnownPeriod(value) {
    return (
      this.monthBtns.some((b) => b.dataset.month === value) ||
      this.yearBtns.some((b) => b.dataset.year === value)
    );
  }

  writeHash() {
    const parts = [];
    // « tout » doit être explicite, sinon un rechargement après élargissement
    // réappliquerait le mois par défaut.
    if (this.period !== this.defaultPeriod) {
      parts.push('mois=' + encodeURIComponent(this.period || 'tout'));
    }
    if (this.tag) parts.push('theme=' + encodeURIComponent(this.tag));

    const url =
      window.location.pathname + window.location.search + (parts.length ? '#' + parts.join('&') : '');
    window.history.replaceState(null, '', url);
  }

  /* ── Pages suivantes ──────────────────────────────────────── */

  loadRemainingPages() {
    const pages = parseInt(this.dataset.pages, 10) || 1;
    const base = this.dataset.blogUrl;
    if (pages < 2 || !base) {
      this.markComplete();
      return;
    }

    const requests = [];
    for (let page = 2; page <= pages; page += 1) {
      requests.push(
        fetch(base + '?page=' + page + '&view=claude-items', { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      );
    }

    // Une page en échec est ignorée sans casser le reste : on conserve les
    // podcasts les plus récents, un filtre fonctionnel dessus, et la
    // pagination serveur reste visible pour atteindre les autres.
    Promise.all(requests).then((chunks) => {
      this.absorb(chunks);
      this.markComplete();
    });
  }

  absorb(chunks) {
    const parser = new DOMParser();
    const fragment = document.createDocumentFragment();
    let added = 0;

    chunks.forEach((html) => {
      if (!html) return;
      const doc = parser.parseFromString(html, 'text/html');
      doc.querySelectorAll('.podcast-item').forEach((node) => {
        this.normalize(node);
        fragment.appendChild(document.importNode(node, true));
        added += 1;
      });
    });

    if (!added) return;

    this.itemsEl.appendChild(fragment);
    this.refreshNodes();
    this.augmentChrono();

    const pagination = this.querySelector('.pagination-wrapper');
    if (pagination) pagination.hidden = true;

    this.apply();
  }

  // La vue de fetch ne connaît pas les réglages de la section : on les
  // réapplique ici pour que les pages absorbées soient identiques à la page 1.
  normalize(node) {
    const index = node.querySelector('.podcast-item__index');
    if (index) index.hidden = this.dataset.showIndex !== '1';
    const date = node.querySelector('.podcast-item__date');
    if (date) date.hidden = this.dataset.showDate !== '1';
  }

  augmentChrono() {
    if (!this.yearsEl) return;

    const known = {};
    this.monthBtns.forEach((btn) => {
      known[btn.dataset.month] = true;
    });

    const missing = [];
    this.items.forEach((item) => {
      const month = item.dataset.month;
      if (month && !known[month]) {
        known[month] = true;
        missing.push(month);
      }
    });

    if (!missing.length) return;

    // Décroissant, comme l'arbre rendu par Liquid : les années anciennes
    // s'ajoutent en bas, repliées. Croissance additive, pas de reconstruction.
    missing.sort().reverse();
    missing.forEach((month) => this.insertMonth(month));
    this.refreshNodes();
    this.syncExpanded();
  }

  insertMonth(monthKey) {
    const year = monthKey.slice(0, 4);
    const row = this.yearsEl.querySelector('[data-year-row="' + year + '"]') || this.createYearRow(year);
    const list = row.querySelector('.podcast-chrono__months');
    if (!list) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'podcast-chrono__month';
    button.dataset.month = monthKey;
    button.setAttribute('aria-pressed', 'false');

    const name = document.createElement('span');
    name.className = 'podcast-chrono__month-name';
    name.textContent = this.monthName(monthKey);

    const spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = ' ' + year;

    const count = document.createElement('span');
    count.className = 'podcast-chrono__count';
    count.setAttribute('aria-hidden', 'true');

    button.append(name, spoken, count);

    const item = document.createElement('li');
    item.appendChild(button);

    const after = Array.from(list.children).find((child) => {
      const sibling = child.querySelector('.podcast-chrono__month');
      return sibling && sibling.dataset.month < monthKey;
    });
    list.insertBefore(item, after || null);
  }

  createYearRow(year) {
    const row = document.createElement('li');
    row.className = 'podcast-chrono__year';
    row.dataset.yearRow = year;

    const panelId = 'PodcastYear-added-' + year;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'podcast-chrono__year-toggle';
    toggle.dataset.year = year;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', panelId);

    const label = document.createElement('span');
    label.textContent = year;

    const chevron = document.createElement('span');
    chevron.className = 'podcast-chrono__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    toggle.append(label, chevron);

    const list = document.createElement('ul');
    list.className = 'podcast-chrono__months list-unstyled';
    list.id = panelId;
    list.setAttribute('role', 'list');
    list.hidden = true;

    row.append(toggle, list);

    const after = Array.from(this.yearsEl.children).find((child) => child.dataset.yearRow < year);
    this.yearsEl.insertBefore(row, after || null);
    return row;
  }

  markComplete() {
    this.dataset.complete = 'true';
  }
}

if (!customElements.get('podcast-filters')) {
  customElements.define('podcast-filters', PodcastFilters);
}
